import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Icon, ModalDialog, TextField, Success, PlainButton } from "@culturehq/components";

import { useProfile } from "app/context";
import { isIntuitive, isRTX, Trans } from "app/utils";

import ChqModalDialog from "components/common/ChqModalDialog";

interface EmbedSingleStoryProps {
  onClose: () => void;
}

const EMBBED_SLIDER_CODE = "<div id=\"IDENTIFIER\" /><script type=\"text/javascript\">+function (s, t, q) { (y = document.createElement(\"script\")).type = \"text/javascript\"; y.src = \"https://assets.culturehq.com/single-story-widget-job-mappings_1732564233.js\"; y.onload = function () { CHQ(s, t, q) }; document.getElementsByTagName(\"head\")[0].appendChild(y); }(\"#IDENTIFIER\", \"null\", FILTERS)</script>";

const replaceEmbbededCode = (
  filters: Record<string, any>
// eslint-disable-next-line max-len
) => EMBBED_SLIDER_CODE.replaceAll("IDENTIFIER", `chq_${Date.now()}`).replace("FILTERS", JSON.stringify(filters));

const EmbedSingleStoryJobMappings = ({ onClose: onCloseMenu }: EmbedSingleStoryProps) => {
  const { profile: { organization } } = useProfile();
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const externalMapperIdentifiers = useMemo(() => (
    organization.externalMappers?.map(
      externalMapper => externalMapper.externalHtmlIdentifier
    ) || []
  ), [organization]);

  const [embeddableCode, setEmbeddableCode] = useState<string>();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const elements = document.querySelectorAll(".copySringId, .copyCode");
      const isOutsideClick = Array.from(elements).every(
        element => !element.contains(event.target as Node)
      );
      if (isOutsideClick) setCopied(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(
    () => {
      const replacebleQuery = {
        org: organization.id,
        identifiers: externalMapperIdentifiers,
        ...((isRTX(organization.name) || isIntuitive(organization.name)) && { map_strategy: "and_strategy" }),
        ...(isIntuitive(organization.name) && { identifiers_format: "json_format" })
      };
      setEmbeddableCode(replaceEmbbededCode(replacebleQuery));
    }, [externalMapperIdentifiers, organization]
  );

  const onOpen = useCallback(() => setOpen(true), [setOpen]);
  const onClose = useCallback(
    () => {
      setCopied(false);
      setOpen(false);
      if (onCloseMenu) {
        onCloseMenu();
      }
    },
    [onCloseMenu, setCopied, setOpen]
  );

  const copyToClipboard = (
    selector: string,
    setCopiedState: (copied: boolean) => void
  ) => {
    const element = document.querySelector(selector) as HTMLInputElement | null;
    if (element) {
      element.select();
      document.execCommand("copy");
      setCopiedState(true);
    }
  };

  const onCopy = useCallback(() => {
    copyToClipboard("#embeddable_slider_code", setCopied);
  }, []);

  return (
    <>
      <PlainButton onClick={onOpen}>
        <Icon icon="page" /><Trans>Single Story with Job mappings</Trans>
      </PlainButton>
      {open && (
        <ChqModalDialog onClose={onClose} className="EmbedStories">
          <ModalDialog.Heading onClose={onClose}>
            <Trans>Embed Single Story with Job Mappings</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <span className="chq-ffd--sl--place">Embeddable Code</span>
            <TextField
              readOnly
              rows={4}
              name="embeddable_slider_code"
              value={embeddableCode}
            >
              <></>
            </TextField>
            {copied && (
              <Success>
                <Trans>Embeddable code copied to the clipboard.</Trans>
              </Success>
            )}
            <div className="ButtonContainer">
              <Button className="copyCode" primary onClick={onCopy}>
                <Icon icon="copy" />
                <Trans>Copy Code</Trans>
              </Button>
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default EmbedSingleStoryJobMappings;
